import { Button } from '@compass/components'
import { buildDistributionCsvRows, DistributionExportRow } from 'helpers/chartExport/distributionCsv'
import { useChartCsvExport, useIsChartCsvExportEnabled } from 'hooks/useChartCsvExport'
import { DownloadIcon } from 'lucide-react'
import { DateTime } from 'luxon'

export type DistributionExportButtonProps = {
  page: string
  widget: string
  dimension: string
  getRows: () => DistributionExportRow[]
  hasData?: boolean
  secondaryColumns?: string[]
  totalCount?: number | string
  filtersApplied?: boolean
  /** Overrides for pages whose effective range differs from the raw picker selection. */
  from?: DateTime
  to?: DateTime
}

export function DistributionExportButton({
  page,
  widget,
  dimension,
  getRows,
  hasData = true,
  secondaryColumns,
  totalCount,
  filtersApplied,
  from,
  to,
}: DistributionExportButtonProps) {
  const isEnabled = useIsChartCsvExportEnabled()
  const exportCsv = useChartCsvExport()

  const handleExport = () => {
    const rows = getRows()
    if (rows.length === 0) {
      return
    }

    exportCsv({
      page,
      widget,
      qualifier: dimension,
      widgetType: 'distribution',
      rows: buildDistributionCsvRows({ rows, secondaryColumns, totalCount }),
      dataRowCount: rows.length,
      filtersApplied,
      from,
      to,
    })
  }

  if (!isEnabled) {
    return null
  }

  return (
    <Button
      variant='ghost'
      size='sm'
      isIcon
      isDisabled={!hasData}
      onPress={handleExport}
      aria-label={hasData ? `Export ${widget} as CSV` : 'Export unavailable'}
      tooltip={hasData ? 'Export as CSV' : undefined}
      data-testid={`chart-export-${page}-${widget}`.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
      data-feature-highlight='distribution-csv-export'
    >
      <DownloadIcon />
    </Button>
  )
}
